import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useLocation } from 'react-router-dom'
import WebService from '@util/webService'
import { truncate } from '@util/helper'
// MUI Table
import MUITable from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableFooter from '@mui/material/TableFooter'
import { tablePaginationClasses } from '@mui/material/TablePagination'
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import '@scss/App.scss'
import MyTableHead from '@components/MyTableHead'
import MyDialog3 from '@components/MyDialog3'
import useIndicator from '@hooks/useIndicator'
import useAlert from '@hooks/useAlert'
import ChatDialog from './chat-dialog'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
const DEFAULT_PAGE_SIZE = 30

const ReadBadge = ({ count }) => {
  const unread = Number(count) > 0
  const color = unread ? '#b71c1c' : '#b0b6be'
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', color: color }}>
      <NotificationsNoneOutlinedIcon fontSize="small" />
      {unread ? (
        <Typography variant='body2' sx={{ fontWeight: 'bold', ml: 0.5, color: color }}>
          {count}
        </Typography>
      ) : null}
    </Box>
  )
}

// 컴포넌트 안에서 정의하면 렌더링마다 새 컴포넌트가 되므로 밖으로 뺌
const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme()
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1)

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={e => onPageChange(e, 0)} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={e => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton onClick={e => onPageChange(e, page + 1)} disabled={page >= lastPage} aria-label="next page">
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton onClick={e => onPageChange(e, lastPage)} disabled={page >= lastPage} aria-label="last page">
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}

/**
 * 문의 목록 (매니저/강사 공통)
 * - query: 목록 조회에 추가로 보낼 파라미터 (예: 강사 화면의 lectureYn, userId)
 * - actions: (row, reload) => 관리 칸 내용. 없으면 관리 칸을 표시하지 않음
 * - title: 화면 제목
 */
const ChatList = ({ query = {}, actions, title = '문의 관리' }) => {
  const { more } = useParams()

  const fields = [
    { field: 'id', placeholder: '아이디', visible: more && (more === 'more') },
    { field: 'unreadCount', placeholder: '읽음', visible: true, width: 100 },
    { field: 'uptTime', placeholder: '수정일시', visible: true, width: 200 },
    { field: 'userAlias', placeholder: '작성자', visible: true, width: 300 },
    { field: 'lectureTitle', placeholder: '관련강좌', visible: true, width: 200 },
    { field: 'ctnt', placeholder: '내용', visible: true },
  ]
  const visibleColumns = fields.filter(col => col.visible)
  const hasActions = typeof actions === 'function'

  const [dataList, set_dataList] = useState([])
  const [totalCount, set_totalCount] = useState(0)
  const [pageInfo, set_pageInfo] = useState({ pageNum: 0, pageSize: DEFAULT_PAGE_SIZE })
  const [unreadOnly, set_unreadOnly] = useState(false)
  const [keywordInput, set_keywordInput] = useState('')
  const [keyword, set_keyword] = useState('')
  const chatHandler = useState(false)

  const [openIndicator, closeIndicator, renderIndicator] = useIndicator()
  const { alert, renderAlert } = useAlert()
  const schola = useSelector(state => state.scholaReducer)
  const location = useLocation()
  const curLocationRef = useRef(location)
  const recordRef = useRef(null)

  const fetchChats = async () => {
    const url = `/chat/root/school/${schola.id}`
    openIndicator()
    const resp = await WebService.get(url, {
      pageNum: pageInfo.pageNum,
      pageSize: pageInfo.pageSize,
      unreadYn: unreadOnly ? 'Y' : 'N',
      keyword: keyword,
      ...query,
    })
    closeIndicator()

    if(resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return
    }
    return JSON.parse(resp.repMessage)
  }

  const onLoad = async () => {
    const info = await fetchChats()
    if(info) {
      set_dataList(JSON.parse(info.records))
      set_totalCount(info.totalCount)
    }
  }

  const openChat = row => {
    recordRef.current = row
    chatHandler[1](true)
  }

  const renderCell = (row, col, colIdx) => {
    if(col.field === 'ctnt') {
      return (
        <TableCell key={colIdx} style={{ cursor: 'pointer' }} onClick={() => openChat(row)}>
          <Typography variant='body2' fontWeight='bold'>
            {truncate(row.content, 100)}
          </Typography>
          <Typography variant='body2' style={{ marginTop: 10 }}>
            {truncate(row.lastContent, 100)}
          </Typography>
        </TableCell>
      )
    }
    else if(col.field === 'unreadCount') {
      return (
        <TableCell key={colIdx}>
          <ReadBadge count={row.unreadCount} />
        </TableCell>
      )
    }
    else {
      return (
        <TableCell key={colIdx}>
          <Typography variant='body2'>{row[col.field]}</Typography>
        </TableCell>
      )
    }
  }

  const handleChangePage = (event, newPage) => {
    set_pageInfo({ ...pageInfo, pageNum: newPage })
  }

  const handleChangeRowsPerPage = event => {
    set_pageInfo({ pageNum: 0, pageSize: parseInt(event.target.value, 10) })
  }

  const applySearch = () => {
    set_pageInfo({ pageNum: 0, pageSize: pageInfo.pageSize })
  }

  const onSearch = () => {
    set_keyword(keywordInput.trim())
    applySearch()
  }

  useEffect(() => {
    if(curLocationRef.current.key !== location.key) {
      curLocationRef.current = location
      set_pageInfo({ pageNum: 0, pageSize: DEFAULT_PAGE_SIZE })
    }
  }, [location])

  // query는 페이지에서 객체 리터럴로 넘어와 렌더링마다 바뀌므로 의존성에 넣지 않음
  useEffect(() => {
    onLoad()
  }, [pageInfo])

  return (
    <div>
      {chatHandler[0] ? (
        <MyDialog3
          title={'대화 내용'}
          windowWidth="md"
          dialogHandler={chatHandler}
          placeClose={true}
          component=<ChatDialog record={recordRef.current} dialogHandler={chatHandler} callback={onLoad} />
        />
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <div style={{ paddingRight: 10 }}>
          <h1>{title}</h1>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16 }}>
        <FormControlLabel
          label="읽지 않음만"
          control={
            <Checkbox
              size="small"
              checked={unreadOnly}
              onChange={e => {
                set_unreadOnly(e.target.checked)
                applySearch()
              }}
            />
          }
        />
        <TextField
          size="small"
          placeholder="학생명"
          value={keywordInput}
          onChange={e => set_keywordInput(e.target.value)}
          onKeyDown={e => {
            // 한글 조합 중 Enter는 무시
            if(e.key === 'Enter' && !e.nativeEvent.isComposing) {
              onSearch()
            }
          }}
        />
        <IconButton size="small" onClick={onSearch}>
          <SearchIcon />
        </IconButton>
      </div>
      <div style={{ paddingTop: 20 }}>
        <MUITable>
          <MyTableHead>
            <TableRow>
              {visibleColumns.map(col => (
                <TableCell size='small' key={col.field} style={{ width: col.width ?? null }}>
                  {col.placeholder}
                </TableCell>
              ))}
              {hasActions ? (
                <TableCell size='small' align='center' style={{ width: 300 }}>
                  관리
                </TableCell>
              ) : null}
            </TableRow>
          </MyTableHead>
          <TableBody>
            {dataList.map(row => (
              <TableRow key={row.id}>
                {visibleColumns.map((col, colIdx) => renderCell(row, col, colIdx))}
                {hasActions ? (
                  <TableCell size='small' align='center'>
                    {actions(row, onLoad)}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                sx={{
                  [`& .${tablePaginationClasses.spacer}`]: {
                    display: 'none'
                  },
                  [`& .${tablePaginationClasses.toolbar}`]: {
                    justifyContent: 'center'
                  }
                }}
                rowsPerPageOptions={[10, 20, 30, 50, 100, { label: 'All', value: -1 }]}
                colSpan={visibleColumns.length + (hasActions ? 1 : 0)}
                count={totalCount}
                rowsPerPage={pageInfo.pageSize}
                page={pageInfo.pageNum}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                labelDisplayedRows={({ from, to, count }) => `${from} - ${to} of ${count}`}
                labelRowsPerPage={'페이지당 갯수'}
              />
            </TableRow>
          </TableFooter>
        </MUITable>

        {renderIndicator()}
        {renderAlert()}
      </div>
    </div>
  )
}

export default ChatList
